// ** Modules
import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';

// ** UI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';

// ** Components
import ComponentSharedTextEditor from '@/components/strapi/Shared/ComponentSharedTextEditor';
import { Icon } from '@/components/common/icons';

// ** Types
import type { SxProps, Theme } from '@mui/material/styles';
import type { StrapiComponentElementProps } from '@/types/strapi';
import type { ComponentElementsAccordionCardItem as ElementsAccordionCardItemProps } from '@/types/graphql';

type ComponentElementsAccordionCardItemProps = StrapiComponentElementProps<ElementsAccordionCardItemProps> & {
	expanded: boolean;
	onToggle: () => void;
	contentId: string;
	iconProps?: SxProps<Theme>;
};

const cardPx = { xs: '1.5rem', md: '2rem' };
const cardPt = { xs: '1.5rem', md: '2rem' };

const ComponentElementsAccordionCardItem = ({
	id,
	expanded,
	onToggle,
	contentId,
	iconProps,
	accordionCardDescription,
	accordionCardIcon,
	accordionCardTitle,
	accordionCardBlockEditor,
	accordionCardTextContent,
	accordionCardTextVariant
}: ComponentElementsAccordionCardItemProps) => {
	const editor = accordionCardBlockEditor;
	const textVariant = accordionCardTextVariant || 'Normal';

	const parsedContent = React.useMemo(() => {
		if (textVariant !== 'Normal' && editor?.blockContent) {
			return JSON.parse(`${editor.blockContent}`);
		}
		return accordionCardTextContent;
	}, [accordionCardTextContent, editor?.blockContent, textVariant]);

	return (
		<Box
			id={`accordion-item-${id}`}
			sx={(theme) => ({
				bgcolor: theme.palette.heroLightBackground.main,
				borderRadius: '0.5rem',
				display: 'flex',
				flexDirection: 'column',
				overflow: 'hidden'
			})}
		>
			{/* Header: title + icon */}
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'flex-start',
					px: cardPx,
					pt: cardPt
				}}
			>
				<Typography
					variant="title-md"
					sx={(t) => ({
						color: t.palette.text.primary,
						fontSize: '1.5rem',
						lineHeight: '2rem',
						letterSpacing: '-0.5px',
						[t.breakpoints.up('lg')]: {
							fontSize: '1.5rem',
							lineHeight: '2rem',
							letterSpacing: '-0.5px'
						}
					})}
				>
					{accordionCardTitle}
				</Typography>
				<Box
					sx={(theme) => ({
						color: theme.palette.primary.main,
						flexShrink: 0,
						ml: '1rem'
					})}
				>
					<Icon name={accordionCardIcon?.icon || ''} width={48} height={48} sx={iconProps} />
				</Box>
			</Box>

			{/* Description */}
			<Box sx={{ px: cardPx, pt: '1rem', pb: '1rem' }}>
				<motion.div
					animate={{ height: expanded ? 'auto' : '3.25rem' }}
					transition={{ duration: 0.3, ease: 'easeInOut' }}
					style={{ overflow: 'hidden' }}
				>
					<Typography
						variant="body-md"
						sx={(t) => ({
							color: t.palette.text.primary,
							fontSize: '1.125rem',
							lineHeight: '1.625rem',
							letterSpacing: '0.05px',
							...(!expanded && {
								display: '-webkit-box',
								WebkitLineClamp: 2,
								WebkitBoxOrient: 'vertical',
								overflow: 'hidden'
							})
						})}
					>
						{accordionCardDescription}
					</Typography>
				</motion.div>
			</Box>

			{/* Expandable content */}
			<AnimatePresence initial={false}>
				{expanded && (
					<motion.div
						id={contentId}
						initial={{ height: 0, opacity: 0 }}
						animate={{ height: 'auto', opacity: 1 }}
						exit={{ height: 0, opacity: 0 }}
						transition={{ duration: 0.3, ease: 'easeInOut' }}
						style={{ overflow: 'hidden' }}
					>
						<Box
							className="textBlock"
							sx={{
								px: cardPx,
								pb: '1rem',
								overflow: 'hidden'
							}}
						>
							{textVariant === 'Normal' ? (
								<Typography
									variant="body-md"
									sx={{
										fontSize: '0.875rem',
										lineHeight: '1.375rem',
										letterSpacing: '0.05px',
										color: 'text.secondary',
										whiteSpace: 'pre-line',
										'@media (min-width: 768px)': {
											fontSize: '0.875rem'
										}
									}}
								>
									{accordionCardTextContent}
								</Typography>
							) : (
								<ComponentSharedTextEditor
									blockContent={parsedContent}
									variantBody={editor?.variantBody}
									variantHeading1={editor?.variantHeading1}
									variantHeading2={editor?.variantHeading2}
									variantHeading3={editor?.variantHeading3}
									variantHeading4={editor?.variantHeading4}
									variantHeading5={editor?.variantHeading5}
									variantHeading6={editor?.variantHeading6}
									overrides={{
										paragraph: {
											fontSize: '0.875rem',
											lineHeight: '1.375rem',
											letterSpacing: '0.05px',
											color: 'text.secondary',
											'@media (min-width: 768px)': {
												fontSize: '0.875rem'
											}
										},
										listItem: {
											fontSize: '0.875rem',
											'@media (min-width: 768px)': {
												fontSize: '0.875rem'
											}
										},
										heading: {
											mb: 1
										}
									}}
								/>
							)}
						</Box>
					</motion.div>
				)}
			</AnimatePresence>

			{/* Divider + toggle */}
			<Box sx={{ mt: 'auto' }}>
				<Box
					sx={(theme) => ({
						mx: cardPx,
						height: '1px',
						bgcolor: theme.palette.grey[300]
					})}
				/>
				<ButtonBase
					onClick={onToggle}
					disableRipple
					aria-expanded={expanded}
					aria-controls={expanded ? contentId : undefined}
					aria-label={
						expanded ? `Details einklappen: ${accordionCardTitle}` : `Details ausklappen: ${accordionCardTitle}`
					}
					sx={{
						width: '100%',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						pt: '0.75rem',
						pb: { xs: '1.5rem', md: '2rem' },
						cursor: 'pointer'
					}}
				>
					<motion.div
						animate={{ rotate: expanded ? 180 : 0 }}
						transition={{ duration: 0.3, ease: 'easeInOut' }}
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center'
						}}
					>
						<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden={true}>
							<path
								d="M5 7.5L10 12.5L15 7.5"
								stroke="currentColor"
								strokeWidth="1.25"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</motion.div>
				</ButtonBase>
			</Box>
		</Box>
	);
};

export default ComponentElementsAccordionCardItem;
